import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { CUSTOMER_SOURCES, INDUSTRIES } from '@/modules/Customer/constants'
import {
    importCustomersFromExcel,
    scanBusinessCards,
} from '@/modules/Customer/services'
import type { CardScanResponse } from '@/modules/Customer/types'
import useAuthStore from '@/stores/useAuthStore'
import { paths } from '@/utils/path'
import React, { useCallback, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generateExcel } from './scan-card-excel'
import ScanCardPreviewStep from './scan-card-preview-step'
import {
    ACCEPTED_TYPES,
    EditableCard,
    MAX_FILE_SIZE,
    MAX_FILES,
} from './scan-card-types'
import ScanCardUploadStep from './scan-card-upload-step'

const ScanCards: React.FC = () => {
    const { t } = useTranslation('customer')
    const { t: tGlobal } = useTranslation()
    const navigate = useNavigate()
    const employee = useAuthStore((s) => s.employee)
    const [files, setFiles] = useState<File[]>([])
    const [scanning, setScanning] = useState(false)
    const [importing, setImporting] = useState(false)
    const [cards, setCards] = useState<EditableCard[]>([])
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    // --- File validation & upload handlers ---

    const validateFiles = useCallback(
        (incoming: File[]): File[] => {
            const valid: File[] = []
            for (const f of incoming) {
                if (!ACCEPTED_TYPES.includes(f.type)) {
                    toast.error(`${f.name}: ${tGlobal('invalid-file-type')}`)
                    continue
                }
                if (f.size > MAX_FILE_SIZE) {
                    toast.error(`${f.name}: ${t('scan_card.file_too_large')}`)
                    continue
                }
                valid.push(f)
            }
            const total = files.length + valid.length
            if (total > MAX_FILES) {
                toast.error(t('scan_card.max_files'))
                return valid.slice(0, MAX_FILES - files.length)
            }
            return valid
        },
        [files.length, t, tGlobal]
    )

    const handleFilesAdd = useCallback(
        (newFiles: FileList | File[]) => {
            const validated = validateFiles(Array.from(newFiles))
            if (validated.length) setFiles((prev) => [...prev, ...validated])
        },
        [validateFiles]
    )

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault()
            handleFilesAdd(e.dataTransfer.files)
        },
        [handleFilesAdd]
    )

    // --- Scan API call ---

    const handleScan = async () => {
        if (!files.length) return
        setScanning(true)
        try {
            const res = await scanBusinessCards(files)
            const result: CardScanResponse = res.result
            const currentEmpId = employee?.employee_id
            const editableCards: EditableCard[] = result.results.map(
                (r, idx) => ({
                    _id: String(idx),
                    _filename: r.filename,
                    _error: r.error,
                    customer_name: r.data?.customer_name || '',
                    customer_email: r.data?.customer_email || '',
                    customer_phone: r.data?.customer_phone || '',
                    customer_position: r.data?.customer_position || '',
                    customer_industry: r.data?.customer_industry || '',
                    customer_website: r.data?.customer_website || '',
                    customer_note: r.data?.customer_note || '',
                    company_name: r.data?.company_name || '',
                    company_address: r.data?.company_address || '',
                    company_website: r.data?.company_website || '',
                    company_tax: r.data?.company_tax || '',
                    customer_type: 'individual',
                    customer_source: 'research',
                    customer_sale_followers: currentEmpId ? [currentEmpId] : [],
                })
            )
            setCards(editableCards)
            setSelectedKeys(
                editableCards.filter((c) => !c._error).map((c) => c._id)
            )
            toast.success(
                t('scan_card.success_count', {
                    success: result.success,
                    total: result.total,
                })
            )
        } catch (err: any) {
            toast.error(err?.response?.data?.message || t('scan_card.error'))
        } finally {
            setScanning(false)
        }
    }

    // --- Card edit handlers ---

    const updateCard = (id: string, field: string, value: any) => {
        setCards((prev) =>
            prev.map((c) => (c._id === id ? { ...c, [field]: value } : c))
        )
    }

    const removeCard = (id: string) => {
        setCards((prev) => prev.filter((c) => c._id !== id))
        setSelectedKeys((prev) => prev.filter((k) => k !== id))
    }

    const bulkUpdateCards = (field: string, value: any) => {
        setCards((prev) =>
            prev.map((c) => (c._error ? c : { ...c, [field]: value }))
        )
    }

    const selectedCards = useMemo(
        () => cards.filter((c) => !c._error && selectedKeys.includes(c._id)),
        [cards, selectedKeys]
    )

    const canImport = useMemo(() => {
        if (!selectedCards.length) return false
        return selectedCards.every(
            (c) =>
                c.customer_name &&
                c.customer_email &&
                c.customer_phone &&
                c.customer_source &&
                c.customer_industry &&
                c.customer_sale_followers.length > 0
        )
    }, [selectedCards])

    // --- Import via Excel ---

    const handleImport = async () => {
        if (!canImport) {
            toast.error(t('scan_card.fill_required'))
            return
        }
        setImporting(true)
        try {
            const excelFile = generateExcel(selectedCards)
            const result = await importCustomersFromExcel(excelFile)
            toast.success(result.message)
            navigate(paths.customersList)
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    tGlobal('import-customers-error')
            )
        } finally {
            setImporting(false)
        }
    }

    // --- Select options ---

    const customerSourceOptions = CUSTOMER_SOURCES(tGlobal)
    const industryOptions = INDUSTRIES
    const selectPlaceholder = `${tGlobal('select')}...`

    const goBack = () => navigate(paths.customersList)

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.customersList,
                        title: tGlobal('customer_manager'),
                    },
                    {
                        path: paths.customerScanCards,
                        title: t('scan_card.title'),
                    },
                ]}
            />

            <Paper className="mt-2">
                <ScanCardUploadStep
                    files={files}
                    scanning={scanning}
                    onDrop={handleDrop}
                    onFilesAdd={(fl) => handleFilesAdd(fl)}
                    onRemoveFile={(idx) =>
                        setFiles((prev) => prev.filter((_, i) => i !== idx))
                    }
                    onScan={handleScan}
                    onClose={goBack}
                    t={t}
                    tGlobal={tGlobal}
                />
            </Paper>

            {cards.length > 0 && (
                <Paper className="mt-2">
                    <ScanCardPreviewStep
                        cards={cards}
                        selectedKeys={selectedKeys}
                        canImport={canImport}
                        importing={importing}
                        customerSourceOptions={customerSourceOptions}
                        industryOptions={industryOptions}
                        selectPlaceholder={selectPlaceholder}
                        onSelectionChange={setSelectedKeys}
                        onUpdateCard={updateCard}
                        onBulkUpdate={bulkUpdateCards}
                        onRemoveCard={removeCard}
                        onImport={handleImport}
                        onClose={goBack}
                        t={t}
                        tGlobal={tGlobal}
                    />
                </Paper>
            )}
        </div>
    )
}

export default ScanCards
